<template>
    <div class="fp-page">
        <!-- Animated background -->
        <div class="fp-bg">
            <div class="fp-bg-circle fp-bg-circle-1"></div>
            <div class="fp-bg-circle fp-bg-circle-2"></div>
            <div class="fp-bg-circle fp-bg-circle-3"></div>
        </div>

        <div class="fp-card">
            <!-- Logo -->
            <div class="fp-logo">
                <div class="fp-logo-circle">
                    <img src="../../assets/Img/Logo/MoceroLogo.png" alt="Logo" />
                </div>
                <h1 class="fp-brand">Forgot Password</h1>
                <p class="fp-tagline">Enter your email and we'll help you recover access</p>
            </div>

            <!-- Form -->
            <form @submit.prevent="handleSubmit" class="fp-fields">
                <div class="fp-field">
                    <label for="fp-email">Email Address</label>
                    <div class="fp-input-wrap">
                        <i class="pi pi-envelope fp-input-icon"></i>
                        <input
                            id="fp-email"
                            v-model="email"
                            type="email"
                            placeholder="Enter your email"
                            autocomplete="email"
                            :disabled="loading"
                            required
                        />
                    </div>
                </div>

                <button type="submit" class="fp-submit" :disabled="loading">
                    <i v-if="loading" class="pi pi-spin pi-spinner"></i>
                    <span v-else>Reset Password</span>
                </button>

                <div v-if="message" :class="['fp-message', messageClass]">
                    <i :class="messageClass === 'success' ? 'pi pi-check-circle' : 'pi pi-exclamation-circle'"></i>
                    <span>{{ message }}</span>
                </div>

                <p class="fp-back">
                    <router-link to="/auth/login" class="fp-back-link">
                        <i class="pi pi-arrow-left"></i>
                        Back to Login
                    </router-link>
                </p>
            </form>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import LoginService from '../../services/auth/LoginService'

defineOptions({
    name: 'ForgotPasswordView'
})

const router = useRouter()
const loading = ref(false)
const email = ref('')
const message = ref('')
const messageClass = ref('')

const handleSubmit = async () => {
    message.value = ''
    messageClass.value = ''

    const trimmed = email.value.trim()
    if (!trimmed) {
        message.value = 'Please enter your email address'
        messageClass.value = 'error'
        return
    }

    try {
        loading.value = true
        const result = await LoginService.CheckEmailExists(trimmed)

        if (result?.msg === 'Exists') {
            message.value = 'A password reset request has been raised. Please contact your administrator to receive your new password.'
            messageClass.value = 'success'
            setTimeout(() => router.push('/auth/login'), 4000)
        } else {
            message.value = 'No account found with this email address.'
            messageClass.value = 'error'
        }
    } catch (error) {
        console.error('Password reset error:', error)
        message.value = 'Unable to process your request right now. Please try again.'
        messageClass.value = 'error'
    } finally {
        loading.value = false
    }
}
</script>

<style scoped>
/* ─── Page ────────────────────────────────────────────────────────────────── */
.fp-page {
    min-height: 100vh;
    min-height: 100dvh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f0f4f8;
    position: relative;
    overflow: hidden;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    padding: 24px;
}

/* ─── Animated background blobs ──────────────────────────────────────────── */
.fp-bg { position: absolute; inset: 0; z-index: 0; overflow: hidden; }
.fp-bg-circle {
    position: absolute;
    border-radius: 50%;
    filter: blur(80px);
    opacity: 0.35;
    animation: fp-float 20s ease-in-out infinite;
}
.fp-bg-circle-1 { width: 500px; height: 500px; background: #0d9488; top: -120px; left: -100px; }
.fp-bg-circle-2 { width: 400px; height: 400px; background: #6366f1; bottom: -80px; right: -60px; animation-delay: -7s; }
.fp-bg-circle-3 { width: 300px; height: 300px; background: #f59e0b; top: 50%; left: 50%; animation-delay: -14s; }

@keyframes fp-float {
    0%, 100% { transform: translate(0, 0) scale(1); }
    33% { transform: translate(30px, -30px) scale(1.05); }
    66% { transform: translate(-20px, 20px) scale(0.95); }
}

/* ─── Card ───────────────────────────────────────────────────────────────── */
.fp-card {
    position: relative;
    z-index: 1;
    width: 440px;
    max-width: 100%;
    background: #ffffff;
    border-radius: 20px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.06);
    padding: 48px 40px;
}

/* Logo */
.fp-logo { text-align: center; margin-bottom: 32px; }

.fp-logo-circle {
    width: 72px;
    height: 72px;
    border-radius: 18px;
    background: linear-gradient(135deg, #e6f7f6, #d1fae5);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 16px;
    box-shadow: 0 4px 12px rgba(13, 148, 136, 0.15);
}

.fp-logo-circle img { width: 42px; height: 42px; object-fit: contain; }

.fp-brand {
    font-size: 22px;
    font-weight: 700;
    color: #0f172a;
    margin: 0 0 6px;
}

.fp-tagline {
    font-size: 13px;
    color: #94a3b8;
    margin: 0;
}

/* Fields */
.fp-fields { display: flex; flex-direction: column; gap: 18px; }

.fp-field label {
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: #334155;
    margin-bottom: 6px;
}

.fp-input-wrap {
    position: relative;
    display: flex;
    align-items: center;
}

.fp-input-icon {
    position: absolute;
    left: 14px;
    color: #94a3b8;
    font-size: 15px;
    pointer-events: none;
}

.fp-input-wrap input {
    width: 100%;
    padding: 12px 14px 12px 42px;
    border: 1.5px solid #e2e8f0;
    border-radius: 12px;
    font-size: 14px;
    color: #0f172a;
    background: #f8fafc;
    outline: none;
    transition: border-color 0.2s, box-shadow 0.2s;
}

.fp-input-wrap input:focus {
    border-color: #0d9488;
    box-shadow: 0 0 0 3px rgba(13, 148, 136, 0.1);
    background: #fff;
}

.fp-input-wrap input:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.fp-input-wrap input::placeholder { color: #cbd5e1; }

/* Submit */
.fp-submit {
    width: 100%;
    padding: 13px;
    border: none;
    border-radius: 12px;
    background: linear-gradient(135deg, #0d9488, #0f766e);
    color: #fff;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: transform 0.15s, box-shadow 0.15s;
    box-shadow: 0 4px 14px rgba(13, 148, 136, 0.3);
}

.fp-submit:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(13, 148, 136, 0.4);
}

.fp-submit:active { transform: translateY(0); }
.fp-submit:disabled { opacity: 0.7; cursor: not-allowed; transform: none; }

/* Message */
.fp-message {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 10px 14px;
    border-radius: 10px;
    font-size: 13px;
    line-height: 1.4;
}

.fp-message.success {
    background: #f0fdf4;
    color: #15803d;
    border: 1px solid #bbf7d0;
}

.fp-message.error {
    background: #fef2f2;
    color: #dc2626;
    border: 1px solid #fecaca;
}

.fp-message i { font-size: 16px; margin-top: 1px; }

/* Back to login */
.fp-back {
    text-align: center;
    font-size: 13px;
    margin: 4px 0 0;
}

.fp-back-link {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: #0d9488;
    text-decoration: none;
    font-weight: 500;
}

.fp-back-link:hover { text-decoration: underline; }

/* ─── Responsive ─────────────────────────────────────────────────────────── */
@media screen and (max-width: 480px) {
    .fp-page { padding: 0; align-items: flex-start; background: #fff; }
    .fp-bg { display: none; }
    .fp-card {
        width: 100%;
        min-height: 100dvh;
        border-radius: 0;
        box-shadow: none;
        padding: 60px 24px 40px;
    }

    .fp-logo-circle { width: 64px; height: 64px; border-radius: 16px; }
    .fp-logo-circle img { width: 36px; height: 36px; }
    .fp-brand { font-size: 20px; }
    .fp-submit { padding: 14px; font-size: 16px; }
    .fp-input-wrap input { padding: 14px 14px 14px 42px; font-size: 16px; }
}
</style>
